import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, map, of, switchMap } from 'rxjs';

import { CipId, ImpersonableUser } from '@mobi/rwc-utils-azure-ng-jslib';
import { CIP_ADMIN_ENDPOINT, SKIP_JAP_HEADER_CONTEXT } from '@mobi/rwc-utils-common-ng-jslib';

import { CipUserId } from '../../authentication.service';
import { CIP_CONFIG_ID } from '../cip-auth.config';

interface KeycloakUser {
    username: CipUserId;
    id: CipId;
}

@Injectable({ providedIn: 'root' })
export class CipImpersonationIntegrationService {
    #httpClient = inject(HttpClient);
    #keycloakAdminUrl$ = inject(OidcSecurityService)
        .getConfiguration(CIP_CONFIG_ID)
        .pipe(map(config => new URL(config?.authority as string).origin + CIP_ADMIN_ENDPOINT));

    impersonate(testUser: ImpersonableUser): Observable<void> {
        return this.#getKeycloakId(testUser).pipe(
            switchMap(userId =>
                this.#keycloakAdminUrl$.pipe(
                    map(keycloakAdminUrl => ({ url: `${keycloakAdminUrl}/users/${userId}/impersonation`, userId }))
                )
            ),
            switchMap(({ userId, url }) =>
                this.#httpClient.post<void>(
                    url,
                    { realm: 'cip', user: userId },
                    { withCredentials: true, context: SKIP_JAP_HEADER_CONTEXT.context }
                )
            )
        );
    }

    #getKeycloakId(impersonableUser: ImpersonableUser): Observable<KeycloakUser['id']> {
        if (impersonableUser.cipId) {
            return of(impersonableUser.cipId);
        }
        const userId = impersonableUser.userId.toLowerCase();
        return this.#keycloakAdminUrl$.pipe(
            map(keycloakAdminUrl => ({ url: `${keycloakAdminUrl}/users?username=${userId}`, userId })),
            switchMap(({ userId, url }) =>
                this.#httpClient.get<KeycloakUser[]>(url, SKIP_JAP_HEADER_CONTEXT).pipe(
                    map(users => {
                        const keycloakUser = users.find(u => u?.username === userId);
                        if (!keycloakUser) {
                            throw new Error(`User '${impersonableUser.userId}' not found in Keycloak.`);
                        }
                        return keycloakUser.id;
                    })
                )
            )
        );
    }
}
